import { Chip, Grid, useMediaQuery } from '@mui/material';
import { calculateDuration, dateFormatShortTime, parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import styled from 'styled-components';

import CommentsIcon from '../../../../assets/svg/Plan/report/CommentsIcon';
import CustomFieldsIcon from '../../../../assets/svg/Plan/report/CustomFieldsIcon';
import NobodyAtHomeIcon from '../../../../assets/svg/Plan/report/NobodyAtHomeIcon';
import OtherCancelReasons from '../../../../assets/svg/Plan/report/OtherCancelReasons';
import PhotosIcon from '../../../../assets/svg/Plan/report/PhotosIcon';
import SignatureIcon from '../../../../assets/svg/Plan/report/SignatureIcon';
import WrongAddressIcon from '../../../../assets/svg/Plan/report/WrongAddressIcon';
import { numericToText } from '../../../../components/form-fields/FormTimeWindows';
import { getStopStatusColor } from '../../../../constants/Colors';
import { Driver } from '../../../../domain/driver/Driver';
import { Stop } from '../../../../domain/stop/Stop';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import Text from '../../../../react-components/Text';
import StopCircleColor from '../../../shared/StopCircleColor';
import { stopTimewindowsAreInvalid } from '../components/BrokenConstraintsTooltip';
import { useRetrieveActivePlan } from '../hooks/plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';

interface StopRestrictionChipProps {
  backgroundColor?: string;
}

const STOPCHIP_HEIGHT = 50;
const STOPCHIP_MARGIN = 4;

const StopRestrictionChip = styled(Chip)<StopRestrictionChipProps>`
  height: 18px !important;
  border-radius: 2px !important;
  padding: 2px 4px !important;
  margin-right: 4px !important;
  background-color: ${(props) => props.backgroundColor || RoutalPalette.neutral02} !important;
  & .MuiChip-label {
    padding: 0 !important;
  }
`;

const StopChipContainer = styled(Grid)`
  margin-bottom: ${STOPCHIP_MARGIN}px;
  border: 1px solid ${RoutalPalette.neutral10};
  border-radius: 5px;
  background-color: ${RoutalPalette.neutral02n};
  height: ${STOPCHIP_HEIGHT}px;
  align-content: center !important;
`;

const StopChipHeader = styled(Grid)`
  height: 40px;
`;
const StopHeaderRow = styled(Grid)`
  height: 20px;
  padding-left: 8px;
  padding-right: 8px;
`;

export const PlanGeoFenceStopChip = ({ stop, driver }: { stop: Stop; driver?: Driver }) => {
  const { data: plan } = useRetrieveActivePlan();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();
  const { data: activeProject } = useRetrieveActiveProject();
  const isLargeDesktop = useMediaQuery(`(min-width: 1920px)`);

  const renderCompleted = () => {
    const planReport = (stop.reports ?? []).find((report) => report.plan_id === plan?.id);

    const backgroundColor = RoutalPalette.neutral00;

    const iconColor = getStopStatusColor(stop.status);

    const tooltipErrorBackground = getStopStatusColor(`canceled`);

    return (
      <Grid container direction="row">
        {planReport?.cancel_reason && (
          <RoutalTooltip
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0}
            title={intl.formatMessage({ id: `properties.stop.report.reason.${planReport.cancel_reason}` })}
            placement="right"
            backgroundColor={tooltipErrorBackground}
            arrowColor={tooltipErrorBackground}
          >
            <Grid
              style={{
                height: `18px`,
                padding: `4px 8px`,
                display: `flex`,
                alignItems: `center`,
                position: `relative`,
                borderRadius: `5px`,
                marginRight: `4px`,
                backgroundColor,
              }}
            >
              {(() => {
                if (planReport.cancel_reason === `nobody`) {
                  return (
                    <NobodyAtHomeIcon
                      style={{
                        color: iconColor,
                        height: `16px`,
                        width: `16px`,
                      }}
                    />
                  );
                }
                if (planReport.cancel_reason === `wrong_address`) {
                  return (
                    <WrongAddressIcon
                      style={{
                        color: iconColor,
                        height: `16px`,
                        width: `16px`,
                      }}
                    />
                  );
                }
                return (
                  <OtherCancelReasons
                    style={{
                      color: iconColor,
                      height: `16px`,
                      width: `16px`,
                    }}
                  />
                );
              })()}
            </Grid>
          </RoutalTooltip>
        )}

        {planReport?.comments && (
          <Grid
            style={{
              height: `18px`,
              padding: `4px 8px`,
              display: `flex`,
              alignItems: `center`,
              position: `relative`,
              borderRadius: `5px`,
              marginRight: `4px`,
              backgroundColor,
            }}
          >
            <CommentsIcon color={iconColor} secondaryColor={RoutalPalette.neutral00} />
          </Grid>
        )}

        {planReport?.signature && (
          <Grid
            style={{
              height: `18px`,
              padding: `4px 8px`,
              display: `flex`,
              alignItems: `center`,
              position: `relative`,
              borderRadius: `5px`,
              marginRight: `4px`,
              backgroundColor,
            }}
          >
            <SignatureIcon color={iconColor} />
          </Grid>
        )}

        {(planReport?.images ?? []).length > 0 && (
          <Grid
            style={{
              height: `18px`,
              padding: `4px 8px`,
              display: `flex`,
              alignItems: `center`,
              position: `relative`,
              borderRadius: `5px`,
              marginRight: `4px`,
              backgroundColor,
            }}
          >
            <PhotosIcon color={iconColor} />
          </Grid>
        )}

        {planReport?.custom_fields?.props && Object.keys(planReport.custom_fields.props).length !== 0 && (
          <Grid
            style={{
              height: `18px`,
              padding: `4px 8px`,
              display: `flex`,
              alignItems: `center`,
              position: `relative`,
              borderRadius: `5px`,
              marginRight: `4px`,
              backgroundColor,
            }}
          >
            <CustomFieldsIcon color={iconColor} />
          </Grid>
        )}
      </Grid>
    );
  };

  const renderPending = () => (
    <>
      {/* TODO: WEIGHT ERRORS */}
      {stop.pickup && (
        <StopRestrictionChip
          label={
            <Text
              variant="ultra-small"
              style={{
                fontWeight: 400,
                padding: 0,
                color: RoutalPalette.secondary20,
              }}
              intlId="with_pickup"
            />
          }
        />
      )}
      {stop.time_windows && stop.time_windows.length > 0 && activeProject?.constraints.time_windows && (
        <StopRestrictionChip
          backgroundColor={
            driver?.status === `not_started` && stopTimewindowsAreInvalid(stop)
              ? RoutalPalette.error.dark
              : RoutalPalette.neutral10
          }
          label={
            <Text
              variant="ultra-small"
              style={{
                fontWeight: 400,
                padding: 0,
                color:
                  driver?.status === `not_started` && stopTimewindowsAreInvalid(stop)
                    ? RoutalPalette.neutral00
                    : RoutalPalette.secondary20,
              }}
            >{`${numericToText(stop.time_windows[0])} ${stop.time_windows.length > 1 ? `+` : ``}`}</Text>
          }
        />
      )}
      {stop.duration !== undefined && (
        <StopRestrictionChip
          label={
            <Text
              variant="ultra-small"
              style={{
                fontWeight: 400,
                padding: 0,
                color: RoutalPalette.secondary20,
              }}
            >{`${calculateDuration(stop.duration)} min`}</Text>
          }
        />
      )}
      {/* TODO: WEIGHT ERRORS */}
      {stop.weight && activeProject?.constraints.weight ? (
        <StopRestrictionChip
          label={
            <Text
              variant="ultra-small"
              style={{
                fontWeight: 400,
                padding: 0,
                color: RoutalPalette.secondary20,
              }}
            >{`${Math.floor(stop.weight) !== stop.weight ? stop.weight.toFixed(2) : stop.weight} ${
              activeProject?.units?.weight
            }`}</Text>
          }
        />
      ) : null}
      {/* TODO: VOLUME ERRORS */}
      {stop.volume && activeProject?.constraints.volume ? (
        <StopRestrictionChip
          label={
            <Text
              variant="ultra-small"
              style={{
                fontWeight: 400,
                padding: 0,
                color: RoutalPalette.secondary20,
              }}
            >{`${Math.floor(stop.volume) !== stop.volume ? stop.volume.toFixed(2) : stop.volume} ${
              activeProject?.units?.volume
            }`}</Text>
          }
        />
      ) : null}
    </>
  );

  const renderTime = () => {
    const planReport = (stop.reports ?? []).find((report) => report.plan_id === plan?.id);

    if (planReport && stop.status !== `pending`) {
      return (
        <>
          <Text
            variant="body"
            style={{
              color:
                planReport.type === `service_report_completed`
                  ? RoutalPalette.markerStopColors.completed.medium
                  : RoutalPalette.markerStopColors.canceled.medium,
              fontWeight: 600,
            }}
          >
            {String(new Date(planReport.created_at).getHours()).padStart(2, `0`)}:
            {String(new Date(planReport.created_at).getMinutes()).padStart(2, `0`)}
          </Text>

          {/* {getTimeDiff(planReport.created_at, stop?.planned_arrival_time || 0)} */}
        </>
      );
    }

    if (stop.estimated_arrival_time) {
      return (
        <>
          <Text
            variant="body"
            style={{
              color: RoutalPalette.secondary20,
              fontWeight: 600,
            }}
          >
            {dateFormatShortTime(stop.estimated_arrival_time)}
          </Text>

          {/* {getTimeDiff(stop.estimated_arrival_time, stop?.planned_arrival_time || 0)} */}
        </>
      );
    }

    if (stop.planned_arrival_time || stop.planned_arrival_time === 0) {
      return (
        <Text
          variant="body"
          style={{
            color: RoutalPalette.secondary00,
            fontWeight: 600,
          }}
        >
          {parseSecondsToHM(stop.planned_arrival_time)}
        </Text>
      );
    }

    return null;
  };

  return (
    <StopChipContainer
      container
      alignContent="space-between"
      style={{ backgroundColor: RoutalPalette.neutral02n, position: `relative` }}
    >
      <StopChipHeader container justifyContent="center" flexDirection="column" wrap="nowrap">
        <StopHeaderRow container direction="row" alignItems="center" justifyContent={`space-between`}>
          <Grid container alignItems="center" width={`auto`}>
            <StopCircleColor
              driver={driver}
              status={stop.status}
              isPickup={stop.type === `pickup`}
              number={stop.order != null ? stop.order + 1 : undefined}
              isSelected={false}
              planDrivers={planDrivers}
              size={20}
            />
            <HSpacer small />
            <RoutalTooltip
              mouseEnterDelay={0.5}
              mouseLeaveDelay={0}
              id={stop.id}
              title={stop.label ?? ``}
              placement="top"
            >
              {(stop.label ?? ``) !== `` ? (
                <Text
                  variant="body"
                  style={{
                    fontWeight: isLargeDesktop ? 400 : 300,
                    cursor: `pointer`,
                    textOverflow: `ellipsis`,
                    overflow: `hidden`,
                    whiteSpace: `nowrap`,
                  }}
                >
                  {stop.label}
                </Text>
              ) : (
                <Text
                  variant="body"
                  intlId="no_name"
                  style={{
                    color: RoutalPalette.secondary00,
                    fontWeight: isLargeDesktop ? 400 : 300,
                    cursor: `pointer`,
                  }}
                />
              )}
            </RoutalTooltip>
          </Grid>
          <Grid container width={`auto`}>
            {renderTime()}
          </Grid>
        </StopHeaderRow>
        <StopHeaderRow
          container
          direction="row"
          alignItems="center"
          wrap="nowrap"
          height="16px"
          marginTop="4px"
          style={{ overflow: `hidden` }}
        >
          {stop.status !== `pending` && renderCompleted()}
          {stop.status === `pending` && renderPending()}
        </StopHeaderRow>
      </StopChipHeader>
    </StopChipContainer>
  );
};
